import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const EnterpriseLeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [notes, setNotes] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    minVolume: ''
  });

  useEffect(() => {
    loadLeads();
  }, [filters]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      let query = supabase
        ?.from('enterprise_leads')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filter by volume client-side so NULL values don't silently disappear
      const filtered = filters?.minVolume
        ? (data || []).filter(lead =>
            lead.estimated_monthly_cases != null &&
            lead.estimated_monthly_cases >= parseInt(filters.minVolume)
          )
        : (data || []);

      setLeads(filtered);
    } catch (error) {
      console.error('Load leads error:', error);
      toast?.error('Failed to load enterprise leads');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'contacted' || newStatus === 'qualified') {
        updateData.contacted_at = new Date()?.toISOString();
      }

      const { error } = await supabase
        ?.from('enterprise_leads')
        ?.update(updateData)
        ?.eq('id', leadId);

      if (error) throw error;

      toast?.success('Lead status updated');
      loadLeads();
    } catch (error) {
      console.error('Update lead status error:', error);
      toast?.error('Failed to update lead status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-primary/10 text-primary',
      contacted: 'bg-warning/10 text-warning',
      qualified: 'bg-success/10 text-success',
      converted: 'bg-success text-success-foreground',
      lost: 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getLeadScore = (estimatedCases) => {
    if (estimatedCases >= 500) return { score: 'High', color: 'text-success' };
    if (estimatedCases >= 250) return { score: 'Medium', color: 'text-warning' };
    return { score: 'Low', color: 'text-muted-foreground' };
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <select
              value={filters?.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Minimum Volume</label>
            <select
              value={filters?.minVolume}
              onChange={(e) => setFilters(prev => ({ ...prev, minVolume: e?.target?.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Any Volume</option>
              <option value="11">11+ cases/month</option>
              <option value="26">26+ cases/month</option>
              <option value="51">51+ cases/month</option>
              <option value="100">100+ cases/month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Lead</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Volume</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Message</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2" />
                    Loading leads...
                  </td>
                </tr>
              ) : leads?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    No enterprise leads found
                  </td>
                </tr>
              ) : (
                leads?.map((lead) => {
                  const leadScore = getLeadScore(lead?.estimated_monthly_cases);
                  return (
                    <tr key={lead?.id} className="hover:bg-muted/30 transition-smooth">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">{lead?.full_name}</p>
                          <p className="text-xs text-muted-foreground">{lead?.email}</p>
                          {lead?.phone && <p className="text-xs text-muted-foreground">{lead?.phone}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {lead?.company_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {lead?.estimated_monthly_cases} cases/mo
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-medium ${leadScore?.color}`}>
                          {leadScore?.score}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        {lead?.message ? (
                          <p
                            className="text-sm text-foreground line-clamp-2 cursor-default"
                            title={lead.message}
                          >
                            {lead.message}
                          </p>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">No message</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead?.status)}`}>
                          {lead?.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(lead?.created_at)?.toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={lead?.status}
                          onChange={(e) => updateLeadStatus(lead?.id, e?.target?.value)}
                          className="px-2 py-1 bg-background border border-border rounded text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="qualified">Qualified</option>
                          <option value="converted">Converted</option>
                          <option value="lost">Lost</option>
                        </select>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseLeadManagement;